import {useContext, useEffect, useState} from "react";
import {ApiContext} from "../util/Requests";
import SimpleEditableCard from "../components/cards/SimpleEditableCard";
import EditableEntryInput from "../components/editable_entries/EditableEntryInput";
import PermissionsRequired from "../components/cards/PermissionsRequired";
import TabsSelector from "../components/filters/TabsSelector";
import EditableCardEntryUserMultiselect from "../components/editable_entries/EditableCardEntryUserMultiselect";
import EditCardRibbon from "../components/EditCardRibbon";
import ManagementTree from "../components/ManagementTree";
import FontAwesomeButton from "../components/cards/FontAwesomeButton";
import {faFloppyDisk, faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";
import {toast} from "react-toastify";
import {SC_User} from "../util/api/SC_User";
import {formatNoteDate} from "../util/DateUtils";
import SimpleCardBox from "../components/cards/SimpleCardBox";
import SimpleEvenFlex from "../components/cards/SimpleEvenFlex";
import EditableCardEntryInput from "./editable_entries/EditableCardEntryInput";
import SimpleTableCard from "./cards/SimpleTableCard";
import EditableCardEntryLanguages from "./editable_entries/EditableCardEntryLanguages";
import EditableCardEntryPhone from "./editable_entries/EditableCardEntryPhone";
import SimpleIconsPlate from "./cards/SimpleIconsPlate";
import {FontAwesomeButtonEnumTypes} from "./cards/FontAwesomeButtonEnumTypes";
import EditableCardEntryEmail from "./editable_entries/EditableCardEntryEmail";
import EditableCardEntryDepartment from "./editable_entries/EditableCardEntryDepartment";
import {SC_Department} from "../util/api/enums/SC_Department";

/**
 * @param {SC_User} user
 * @return {JSX.Element}
 */
const UserCard = ({user}) => {
    const [isEditing, setIsEditing] = useState(false);

    const [newEmail, setNewEmail] = useState(user.email);
    const [newDepartments, setNewDepartments] = useState(user.departments);

    const { caches, doRequest } = useContext(ApiContext);

    function saveUser() {
        let formData = new FormData();

        formData.append('user_email', newEmail);
        formData.append('uid', user.insiteID);
        formData.append('department', newDepartments.map(it => it.toAPI()));

        doRequest(formData, 'change_email').then(v => {
            setIsEditing(false);

            caches.employees.update();
        })
    }

    function stopEditing() {
        setIsEditing(false);
        setNewDepartments([]);
    }

    function nukeUser() {
        let formData = new FormData();

        formData.append('uid', user.insiteID);
        formData.append('lock', !user.deactive);

        doRequest(formData, "lock_user").then(v => {
            if (v.code === 200) {
                caches.employees.update();
                toast.info("User deactivated successfully");
            } else if (v.code === 201) {
                caches.employees.update();
                toast.info("User reactivated successfully");
            }
        })
    }

    console.log(newDepartments);

    return <SimpleEditableCard key={user.insiteID}>
        <SimpleEvenFlex>
            <EditableEntryInput type={'text'} baseValue={user.name + ` (${user.paycomID})`} isEditing={false} />
            <SimpleIconsPlate>
                <PermissionsRequired><FontAwesomeButton icon={faTriangleExclamation} buttonType={new FontAwesomeButtonEnumTypes({mainColor: user.deactive ? FontAwesomeButtonEnumTypes.MAINS_COLOR_NONE : FontAwesomeButtonEnumTypes.MAINS_COLOR_RED})} onClick={() => nukeUser()}/></PermissionsRequired>
                <PermissionsRequired><EditCardRibbon isEditing={isEditing} enabled={!user.deactive} disabledMessage={"This user is not active and cannot be modified"} onEdit={() => setIsEditing(true)} onCancel={() => setIsEditing(false)} onSave={saveUser} flatten={true} discrete={false} /></PermissionsRequired>
            </SimpleIconsPlate>
        </SimpleEvenFlex>
        <SimpleEvenFlex>
            <div>
                <EditableCardEntryPhone displayLabel={"Phone #"} baseValue={user.phone} isEditing={false} hyperlinkDisplay={true} />
            </div>
            <div>
                <EditableCardEntryEmail displayLabel={"Email"} baseValue={isEditing ? user.email : newEmail} onEdit={setNewEmail} isEditing={isEditing} />
            </div>
        </SimpleEvenFlex>
        <SimpleEvenFlex>
            <div>
                <EditableCardEntryUserMultiselect fancy={true} isEditing={false} displayLabel={"Manager"} baseValue={user.managers?.length ? user.managers.map(it => it.insiteID) : []}/>
            </div>
        </SimpleEvenFlex>
        <SimpleEvenFlex>
            {user.langs ? <EditableCardEntryLanguages baseValue={user.langs} isEditing={false} /> : null}
        </SimpleEvenFlex>
        <SimpleEvenFlex>
            <div>
                <EditableCardEntryDepartment 
                    displayLabel={"Departments"}
                    baseValue={isEditing ? newDepartments.map(d => d.toAPI()) : user.departments?.map(d => d.toAPI()) ?? []}
                    isEditing={isEditing} 
                    onEdit={(v) => setNewDepartments(v.map(it => SC_Department.fromAPI(it)))}
                />
            </div>
        </SimpleEvenFlex>
        <TabsSelector children={[
            {
                title: "Manage Roles",
                display: user.insiteID && caches.permissions.value.seeNonOwnedData,
                content: <ManageUserRoles key={user.insiteID} existingUser={user} />
            },
            {
                title: "Manage Access Token",
                display: user.insiteID && !user.deactive,
                content: <ManageUserAccessToken key={user.insiteID} existingUser={user} />
            },
            {
                title: "Create Insite User",
                display: false && !user.insiteID,
                content: <CreateUserForm key={user.insiteID} existingUser={user} />
            },
            {
                title: "Management Structure",
                display: user.coOwn || user.ownOwn,
                content: <ManageUserLinks key={user.insiteID} existingUser={user} />
            }
        ]}/>
    </SimpleEditableCard>
}

/**
 * @param {SC_User} existingUser
 * @return {JSX.Element}
 */
const CreateUserForm = ({ existingUser }) => {
    const { caches, doRequest } = useContext(ApiContext);

    const [userFirstName, setUserFirstName] = useState(existingUser.firstName);
    const [userLastName, setUserLastName] = useState(existingUser.lastName);
    const [userEmail, setUserEmail] = useState('');

    const insiteUname = (existingUser.firstName.charAt(0) + existingUser.lastName.toLowerCase() + existingUser.paycomID).replace(/\s/g, '');

    function createUser() {
        let formData = new FormData();

        formData.append("pcid", existingUser.paycomID);
        formData.append("uname", insiteUname);
        formData.append("fn", userFirstName);
        formData.append("ln", userLastName);
        formData.append("email", userEmail);

        doRequest(formData, "create_from_pc").then(v => {
            if (v.code === 200) {
                // should force a refresh all the way around
                caches.employees.update();
            }
        });
    }

    return <div style={{width: "100%"}}><SimpleCardBox defaultMargin={false} defaultPadding={true}>
        <EditableCardEntryEmail hyperlinkDisplay={true} isEditing={true} displayLabel={"User email"} onEdit={setUserEmail} baseValue={userEmail} />
        <SimpleEvenFlex extraStyle={{flexWrap: "wrap"}} growUp={false} enforceEven={false} degradeStackOnMobile={true} >
            <EditableCardEntryInput type={"text"} isEditing={true} displayLabel={"User First Name"} baseValue={userFirstName} onEdit={setUserFirstName} />
            <EditableCardEntryInput type={"text"} isEditing={true} displayLabel={"User Last Name"} baseValue={userLastName} onEdit={setUserLastName} />
        </SimpleEvenFlex>
        <EditableCardEntryInput type={"number"} isEditing={false} displayLabel={"Paycom ID"} baseValue={existingUser.paycomID} />
        <EditableCardEntryInput type={"text"} isEditing={false} displayLabel={"Insite Username"} baseValue={insiteUname} />
        { userEmail ? <button onClick={createUser}>Create Insite User</button> : null }
    </SimpleCardBox></div>
}

const ManageUserRoles = ({existingUser}) => {
    const { doRequest } = useContext(ApiContext);

    const [allRoles, setAllRoles] = useState({});
    const [activeRoles, setActiveRoles] = useState(new Set());

    useEffect(() => {
        let formData = new FormData();

        formData.append("uid", existingUser.insiteID);

        doRequest(formData, "available_roles").then(v => {
            if (v.code === 200) {
                setAllRoles((v.reason.denied.reduce((out, curr) => { out[curr] = false; return out; }, v.reason.granted.reduce((out, curr) => { out[curr] = true; return out; }, {}))));
                setActiveRoles(new Set(v.reason.granted));
            }
        });
    }, []);

    function toTitleCase(str) {
        return str
            .split("_")
            .filter(x => x.length > 0)
            .map((x) => (x.charAt(0).toUpperCase() + x.slice(1)))
            .join(" ");
    }

    function saveRoles() {
        let formData = new FormData();

        formData.append("uid", existingUser.insiteID);
        formData.append("roles", Array.from(activeRoles).join(","));

        doRequest(formData, "set_roles").then(v => {
            if (v.code === 200) {
                setAllRoles((v.reason.denied.reduce((out, curr) => { out[curr] = false; return out; }, v.reason.granted.reduce((out, curr) => { out[curr] = true; return out; }, {}))));
                setActiveRoles(new Set(v.reason.granted));
            }
        });
    }

    const all = Object.entries(allRoles).sort(([a, ], [b, ]) => {
        const aRead = a.startsWith("read");
        const bRead = b.startsWith("read");
        const aWrite = a.startsWith("write");
        const bWrite = b.startsWith("write");

        let aString = a.replace(/^(read|write)_/, '');
        let bString = b.replace(/^(read|write)_/, '');

        /*if ((aRead && bRead) || (aWrite && bWrite)) {
            return aString.localeCompare(bString);
        } else */

        if (aString === bString) {
            if (aRead && bWrite) {
                return -1;
            } else if (aWrite && bRead) {
                return 1;
            }
        }

        console.log(aString, bString);

        return aString.localeCompare(bString);
    });

    console.log(activeRoles, all);

    return <div>
        {all.length ? <SimpleTableCard header={<tr>
            <th>Role</th>
            <th>Granted</th>
        </tr>} body={
            all.map(([key, value]) => {
                console.log(activeRoles.has(key));
                return (
                    <tr>
                        <td>{toTitleCase(key)}</td>
                        <td style={{textAlign: "center"}}>
                            <input
                                type={"checkbox"}
                                defaultValue={activeRoles.has(key)}
                                defaultChecked={activeRoles.has(key)}
                                 onChange={(e) => setActiveRoles((s) => {
                                     console.log(s, e.target.checked);
                                     if (e.target.checked) {
                                         s.add(key)
                                     } else {
                                         s.delete(key)
                                     }

                                     return s;
                                 })}
                            /></td>
                    </tr>
                );
            })
        } footer={<tr>
            <td/><td><FontAwesomeButton icon={faFloppyDisk} onClick={saveRoles}/></td>
        </tr>}/> : <i>You cannot manage this user</i>}
    </div>;
}

/**
 * @param {SC_User} existingUser
 * @return {JSX.Element}
 */
const ManageUserLinks = ({existingUser}) => {
    const { doRequest } = useContext(ApiContext);

    const [isEditing, setIsEditing] = useState(false);
    const [links, setLinks] = useState([]);
    const [managedBy, setManagedBy] = useState([]);
    const [managerOf, setManagerOf] = useState([]);
    const [editedManagedBy, setEditedManagedBy] = useState([]);
    const [editedManagerOf, setEditedManagerOf] = useState([]);

    useEffect(() => {
        setEditedManagedBy(managedBy);
        setEditedManagerOf(managerOf);
    }, [isEditing, managedBy, managerOf]);

    useEffect(() => {
        let formData = new FormData();

        formData.append("uid", existingUser.insiteID);

        doRequest(formData, "get_management_details").then(v => {
            if (v.code === 200) {
                setManagedBy(v.reason.filter(([, id]) => id == existingUser.insiteID).map(([id,]) => id));
                setManagerOf(v.reason.filter(([id, ]) => id == existingUser.insiteID).map(([, id]) => id));
                setLinks(v.reason.map(it => it.map(Number)));
            }
        });
    }, []);

    function submitData() {
        let formData = new FormData();

        formData.append("uid", existingUser.insiteID);
        formData.append("manages", editedManagerOf.join(","));
        formData.append("managed", editedManagedBy.join(","));

        doRequest(formData, "set_management_details").then((v) => {
            if (v.code === 200) {
                setManagedBy(v.reason.filter(([, id]) => id == existingUser.insiteID).map(([id, ]) => id));
                setManagerOf(v.reason.filter(([id, ]) => id == existingUser.insiteID).map(([, id]) => id));
                setLinks(v.reason.map(it => it.map(Number)));

                setIsEditing(false);

                toast.info("Management updated successfully");
            }
        })
    }

    return <SimpleCardBox defaultMargin={false} defaultPadding={true} showOverflow={true}>
        <div style={{display: "flex"}}>
            <div style={{flexGrow: 1}}>
                <SimpleEvenFlex growUp={false} degradeStackOnMobile={true} enforceEven={true} fullWidth={true}>
                    <EditableCardEntryUserMultiselect
                        displayLabel={"Managed By"}
                        baseValue={editedManagedBy}
                        onEdit={setEditedManagedBy}
                        isEditing={isEditing}
                    />
                    {existingUser.coOwn ?
                    <div>
                    <EditableCardEntryUserMultiselect
                        displayLabel={"Manages"}
                        baseValue={editedManagerOf}
                        onEdit={setEditedManagerOf}
                        isEditing={isEditing}
                        showPCID={true}
                    />
                    <ManagementTree
                        links={links}
                        rootUserId={existingUser.insiteID}
                    />
                    </div>: <i>User cannot manage others</i>}
                </SimpleEvenFlex>
            </div>
            <div>
                <EditCardRibbon
                    isEditing={isEditing}
                    onEdit={() => setIsEditing(true)}
                    onCancel={() => setIsEditing(false)}
                    onSave={submitData}
                />
            </div>
        </div>
    </SimpleCardBox>;
}

const ManageUserAccessToken = ({existingUser}) => {
    const { doRequest } = useContext(ApiContext);

    const [reqComplete, setReqComplete] = useState(false);
    const [activePat, setActivePat] = useState(false);
    const [legalPat, setLegalPat] = useState(true);

    useEffect(() => {
        let formData = new FormData();

        formData.append("uid", existingUser.insiteID);

        doRequest(formData, "get_pat_info").then(v => {
            setReqComplete(true);

            if (v.code === 203) {
                setLegalPat(false);
            } else if (v.code === 204) {
                setActivePat(false);
            } else if (v.code === 200) {
                setActivePat(v.reason);
            }
        });
    }, []);

    function issuePAT(method) {
        let formData = new FormData();

        formData.append("uid", existingUser.insiteID);
        formData.append("method", method);

        doRequest(formData, "issue_pat").then(v => {
            if (v.code === 200) {
                toast.info("Pat sent successfully");
            }
        });
    }

    function revokePAT() {
        let formData = new FormData();

        formData.append("uid", existingUser.insiteID);

        doRequest(formData, "revoke_pat").then(v => {
            if (v.code === 200) {
                setActivePat(false);
            }
        });
    }

    return <div style={{width: '100%'}}>
        <EditableCardEntryInput isEditing={false} displayLabel={"Active PAT"} baseValue={activePat ? "Yes" : "No"} />
        <EditableCardEntryInput isEditing={false} displayLabel={"Last Issued Time"} baseValue={activePat.lastIssueTime ? formatNoteDate(new Date(activePat.lastIssueTime)) : "Never Issued"} />
        {reqComplete ?
            legalPat ? (
                <>
                    <button style={{marginRight: '10px'}} onClick={() => issuePAT("email")}>Email PAT</button>
                    <button style={{background: "darkgray", marginRight: '10px'}}>Text PAT</button>
                    <button style={activePat ? {} : {background: "darkgray"}} onClick={() => revokePAT()}>Revoke PAT</button>
                </>
            ) : <i>This user is not permitted to have Personal Access Tokens for security</i>
        : <i>Loading</i>}
        {activePat ? <SimpleTableCard header={<tr>
            <th>Used Time</th>
            <th>Route</th>
            <th>IP</th>
        </tr>} body={<>
        {activePat?.usageHistory.sort((a, b) => new Date(b.time) - new Date(a.time)).map(it => (<tr>
            <td>{formatNoteDate(new Date(it.time))}</td>
            <td>{it.route}</td>
            <td>{it.ip >> 24 & 0xFF}.{it.ip >> 16 & 0xFF}.{it.ip >> 8 & 0xFF}.{it.ip & 0xFF}</td>
        </tr>)) ?? null}
        </>} /> : null}
    </div>;
}

export default UserCard;
